"use client";

import React from "react";
import { MoreHorizontal, Trash2, Pencil } from "lucide-react";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { FeedType, ProductType, DirectoryType } from "./interfaces";
import { useAuth } from "@/hooks/useAuth";

export type User = {
  id: number;
  g_id?: string | null;
  d_id?: string | null;
  i_id?: string | null;
  phone_number: string | null;
  username: string | null;
  role: string;
  sponsor_name: string | null;
  sponsor_code: string | null;
  distributor_code: string | null;
  country: string | null;
  country_code: string | null;
  gender: string | null;
  created_at: string;
  updated_at: string | null;
  image_url: string | null;
  referral_code: string | null;
  distributor_rank: string | null;
  member_name: string | null;
  sponsor_rank: string | null;
  email: string | null;
  referrer?: User | null;
};

export type Notification = {
  id: number;
  title: string;
  body: string;
  type: string;
  target_user_id: number | null;
  sender_id: number | null;
  created_at: string; // ISO timestamp
};

import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FeedEditDialog from "@/pages/dashboard/feed/FeedEditDialog";
import NotificationEditDialog from "@/pages/dashboard/notification/NotificationEditDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Shared date-time formatter: 02-Oct-2025 21:05
const formatDateTime = (input: string | Date): string => {
  if (!input) return "";
  const d = new Date(input);
  if (isNaN(d.getTime())) return String(input);
  const dd = String(d.getDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] as const;
  const MMM = months[d.getMonth()];
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${MMM}-${yyyy} ${hh}:${mm}`;
};

export const categoryColumns: ColumnDef<{ id: number; name: string }>[] = [
  {
    accessorKey: "id",
    header: "Category ID",
  },
  {
    accessorKey: "name",
    header: "Category Name",
  },
];

export const userColumns: ColumnDef<User>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "username",
    header: "Username",
  },
  {
    accessorKey: "phone_number",
    header: "Phone",
  },
  {
    accessorKey: "role",
    header: "Role",
  },
  {
    accessorKey: "country",
    header: "Country",
  },
  {
    accessorKey: "created_at",
    header: "Created At",
  },
];

export const notificationColumns: ColumnDef<Notification>[] = [
  {
    accessorKey: "title",
    header: "Notification Title",
  },
  {
    accessorKey: "body",
    header: "Body",
    cell: ({ row }) => {
      const body = row.getValue("body") as string;
      return <span className="truncate max-w-[28ch] inline-block" title={body}>{body}</span>;
    },
  },
  {
    accessorKey: "type",
    header: "Type",
  },
  {
    accessorKey: "target_user_id",
    header: "Target User",
    cell: ({ row }) => {
      const id = row.getValue("target_user_id") as number | null;
      return <span>{id ?? "All"}</span>;
    },
  },
  {
    accessorKey: "created_at",
    header: "Created",
    cell: ({ row }) => {
      const ts = row.getValue("created_at") as string;
      return <span>{formatDateTime(ts)}</span>;
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const notification = row.original;
      const { user } = useAuth();
      const [open, setOpen] = React.useState(false);
      const [openConfirm, setOpenConfirm] = React.useState(false);
      const [updating, setUpdating] = React.useState(false);
      const confirmActionButtonRef = React.useRef<() => void>(() => {});
      
      const [form, setForm] = React.useState({
        title: notification.title || "",
        body: notification.body || "",
        type: notification.type || "",
        target_user_id: notification.target_user_id || 0,
        sender_id: notification.sender_id || 0,
      });

      // Check if user has delete permission
      const canDelete = user?.email?.toLowerCase() !== "notification@qjskills.com";

      async function handleDelete() {
        try {
          await (await import("@/api/axios")).axiosPrivate.delete(`/notifications/${notification.id}`);
          toast.success("Notification deleted");
          window.location.reload();
        } catch (error) {
          toast.error("Failed to delete notification");
        }
      }

      async function handleSave(latest?: any) {
        try {
          setUpdating(true);
          const working = latest ? { ...latest } : { ...form };
          
          // Clean payload
          const payload: any = { ...working };
          payload.target_user_id = Number(payload.target_user_id) || 0;
          payload.sender_id = Number(payload.sender_id) || 0;
          
          // Trim string fields
          ["title", "body", "type"].forEach(k => {
            if (payload[k] !== undefined && payload[k] !== null) {
              payload[k] = String(payload[k]).trim();
            }
          });

          const api = (await import("@/api/axios")).axiosPrivate;
          await api.put(`/notifications/${notification.id}`, payload);
          
          setOpen(false);
          toast.success("Notification updated");
          window.location.reload();
        } catch (error) {
          toast.error("Failed to update notification");
        } finally {
          setUpdating(false);
        }
      }

      return (
        <>
          <ActionsMenu
            onEdit={() => setOpen(true)}
            onDelete={canDelete ? handleDelete : undefined}
            onDeleteConfirm={canDelete ? (proceed) => {
              confirmActionButtonRef.current = proceed;
              setOpenConfirm(true);
            } : undefined}
            disabledDelete={!canDelete}
          />
          <NotificationEditDialog
            open={open}
            onOpenChange={setOpen}
            form={form}
            setForm={setForm}
            onSave={handleSave}
          />
          <ConfirmDialog
            actionButtonRef={confirmActionButtonRef}
            open={openConfirm}
            onOpenChange={setOpenConfirm}
            isLoading={false}
            AlertdialogDescription="This will permanently delete this notification. Are you sure?"
          />
        </>
      );
    },
  },
];

export const productColumns: ColumnDef<ProductType>[] = [
  {
    accessorKey: "id",
    header: "Product ID",
  },
  {
    id: "thumbnail",
    header: "Thumbnail",
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      const thumb = row.original.thumbnail_url || row.original.image_url;
      return thumb ? (
        <img src={thumb} alt={name} className="size-12 rounded object-cover" />
      ) : (
        <span className="text-muted-foreground">N/A</span>
      );
    },
  },
  {
    accessorKey: "name",
    header: "Product Name",
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      return <span>{name}</span>;
    },
  },
  {
    accessorKey: "category_name",
    header: "Category",
    cell: ({ row }) => {
      const categoryName = row.getValue("category_name") as string;
      return <span>{categoryName || "N/A"}</span>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
  },
  {
    accessorKey: "created_at",
    header: "Added",
    cell: ({ row }) => {
      const date = row.getValue("created_at") as Date;
      return <span>{new Date(date).toLocaleDateString()}</span>;
    },
  },
];

export const directoryColumns: ColumnDef<DirectoryType>[] = [
  { accessorKey: "id", header: "ID" },
  { accessorKey: "country", header: "Country" },
  { accessorKey: "name", header: "Company",
    cell: ({ row }) => {
      const original = row.original as DirectoryType;
      const value = row.getValue("name") as string | null
        || (original as any).company_name
        || (original as any).office_name
        || "N/A";
      return <span>{value}</span>;
    },
  },
  { accessorKey: "person", header: "Person" },
  { accessorKey: "position", header: "Position",
    cell: ({ row }) => <span>{row.getValue("position") || "N/A"}</span>,
  },
  { accessorKey: "phone1", header: "Phone 1",
    cell: ({ row }) => <span>{row.getValue("phone1") || "N/A"}</span>,
  },
  { accessorKey: "email1", header: "Email 1",
    cell: ({ row }) => <span>{row.getValue("email1") || "N/A"}</span>,
  },
  { accessorKey: "city", header: "City",
    cell: ({ row }) => <span>{row.getValue("city") || "N/A"}</span>,
  },
  { accessorKey: "province_state", header: "Province/State",
    cell: ({ row }) => <span>{row.getValue("province_state") || "N/A"}</span>,
  },
  {
    accessorKey: "price_list",
    header: "Price List",
    cell: ({ row }) => {
      const url = row.getValue("price_list") as string | null;
      return url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
          Open
        </a>
      ) : (
        <span className="text-muted-foreground">N/A</span>
      );
    },
  },
];

export const feedColumns: ColumnDef<FeedType>[] = [
  {
    accessorKey: "id",
    header: "Feed Id",
  },
  {
    accessorKey: "title",
    header: "Post Title",
  },
  {
    accessorKey: "type",
    header: "Post Type",
  },
  {
    accessorKey: "category_name",
    header: "Category",
    cell: ({ row }) => {
      const original: any = row.original;
      const name = original.category_name || original.category?.name;
      // Show numeric id fallback if we have id but not resolved name
      if (!name && original.category_id) {
        return <span className="text-muted-foreground">#{original.category_id}</span>;
      }
      return <span>{name || "N/A"}</span>;
    },
  },
  {
    accessorKey: "created_at",
    header: "Added on",
    cell: ({ row }) => {
      const ts = row.getValue("created_at") as string | Date;
      return <span>{formatDateTime(ts)}</span>;
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const feed = row.original;
      const { user } = useAuth();
      const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const [updating, setUpdating] = React.useState(false);
      const [form, setForm] = React.useState({
        title: feed.title || "",
        description: feed.description || "",
        content: feed.content || "",
        media_url: feed.media_url || "",
        thumbnail_url: (feed as any).thumbnail_url || "",
        type: feed.type || "post",
        category_id: feed.category_id || 0,
        tags: feed.tags || "",
        is_featured: !!feed.is_featured,
        author: (feed as any).author || "",
        source: (feed as any).source || "",
        subcategory: (feed as any).subcategory || "",
      });

      // Check if user has delete permission
      const canDelete = user?.email?.toLowerCase() !== "feeds@qjskills.com";

      async function handleDelete() {
        try {
          await (await import("@/api/axios")).axiosPrivate.delete(`/feeds/${feed.id}`);
          window.location.reload();
        } catch (error) {
          alert("Something went wrong on the server side");
        }
      }
    async function handleSave(latest?: any) {
        try {
      setUpdating(true);
      const working = latest ? { ...latest } : { ...form };
      // Normalize payload
      const payload: any = { ...working };
          // Ensure numeric category_id
      payload.category_id = Number(payload.category_id) || 0;
          // Trim string fields
          ["title","description","content","tags","author","source"].forEach(k=>{
            if (payload[k] !== undefined && payload[k] !== null) payload[k] = String(payload[k]).trim();
          });
          // Remove empty optional fields to avoid 422
          ["thumbnail_url","tags","author","source"].forEach(k=>{
            if (payload[k] === "") delete payload[k];
          });
      if (payload.subcategory === "") delete payload.subcategory;
      // Backend may expect boolean; ensure proper type
      payload.is_featured = !!payload.is_featured;
          const api = (await import("@/api/axios")).axiosPrivate;
          await api.put(`/feeds/${feed.id}`, payload);
          setOpen(false);
          
          // Force refresh of feeds data
          try {
            // First try react-query invalidation
            await queryClient.invalidateQueries({ queryKey: ["feeds"] });
            // Also force refetch
            await queryClient.refetchQueries({ queryKey: ["feeds"] });
            toast.success("Feed updated");
          } catch (refetchError) {
            // If react-query fails, fall back to page reload
            toast.success("Feed updated");
            window.location.reload();
          }
        } catch (error) {
          alert("Failed to update feed.");
        }
        finally {
          setUpdating(false);
        }
      }
      const [openConfirm, setOpenConfirm] = React.useState(false);
      const confirmActionButtonRef = React.useRef<() => void>(() => {});

      return (
        <>
          <ActionsMenu
            onEdit={() => setOpen(true)}
            onDelete={canDelete ? handleDelete : undefined}
            onDeleteConfirm={canDelete ? (proceed) => {
              confirmActionButtonRef.current = proceed;
              setOpenConfirm(true);
            } : undefined}
            disabledDelete={!canDelete}
          />
          <FeedEditDialog
            open={open}
            onOpenChange={setOpen}
            form={form}
            setForm={setForm}
            onSave={handleSave}
          />
          <ConfirmDialog
            actionButtonRef={confirmActionButtonRef}
            open={openConfirm}
            onOpenChange={setOpenConfirm}
            isLoading={false}
            AlertdialogDescription="This will permanently delete this feed. Are you sure?"
          />
        </>
      );
    },
  },
];

// Influencer Submission Columns
import { InfluencerSubmission, SubmissionStatus } from "./interfaces";

const statusColors: Record<SubmissionStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

export const influencerSubmissionColumns: ColumnDef<InfluencerSubmission>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "caption",
    header: "Caption",
    cell: ({ row }) => {
      const caption = row.getValue("caption") as string;
      return <span className="truncate max-w-[25ch] inline-block" title={caption}>{caption}</span>;
    },
  },
  {
    accessorKey: "platform",
    header: "Platform",
    cell: ({ row }) => {
      const platform = row.getValue("platform") as string;
      return <span className="capitalize">{platform}</span>;
    },
  },
  {
    accessorKey: "influencer_id",
    header: "Influencer",
    cell: ({ row }) => {
      const influencer = row.original.influencer;
      return <span>{influencer?.username || influencer?.email || `#${row.original.influencer_id}`}</span>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as SubmissionStatus;
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || "bg-gray-100 text-gray-800"}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    },
  },
  {
    accessorKey: "media",
    header: "Media",
    cell: ({ row }) => {
      const media = row.original.media || [];
      return <span>{media.length} item(s)</span>;
    },
  },
  {
    accessorKey: "edited_by_admin",
    header: "Edited",
    cell: ({ row }) => {
      return row.original.edited_by_admin ? (
        <span className="text-blue-600">Yes</span>
      ) : (
        <span className="text-muted-foreground">No</span>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: "Submitted",
    cell: ({ row }) => {
      const ts = row.getValue("created_at") as string;
      return <span>{formatDateTime(ts)}</span>;
    },
  },
];
